import { useState } from "react";
import { post } from "@/lib/api";

export type DiscussionPost = {
  id: string;
  parentId: string | null;
  body: string;
  authorName: string;
  mine: boolean;
  at: string;
  replies?: DiscussionPost[];
};

export type DiscussionState = {
  board: {
    allowPromptResponse: boolean;
    allowReplyToOthers: boolean;
    requirePromptBeforeViewing: boolean;
  };
  postedPrompt: boolean;
  canSeeOthers: boolean;
  othersHiddenCount: number;
  satisfied: boolean;
  required: { replyToPrompt: boolean; replyToOthers: number };
  posts: DiscussionPost[];
};

function whenLabel(iso: string) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  return then.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function PostCard({
  post,
  allowReply,
  onReply,
  nested = false,
}: {
  post: DiscussionPost;
  allowReply: boolean;
  onReply?: (parentId: string, body: string) => Promise<void>;
  nested?: boolean;
}) {
  const replies = post.replies || [];
  const replyCount = replies.length;
  const [reply, setReply] = useState("");
  const [open, setOpen] = useState(false);
  const [showReplies, setShowReplies] = useState(nested);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!onReply || !reply.trim() || busy) return;
    setBusy(true);
    try {
      await onReply(post.id, reply);
      setReply("");
      setOpen(false);
      setShowReplies(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className={`student-dpost${nested ? " student-dpost--reply" : ""}`}>
      <header className="student-dpost__head">
        <span className="student-dpost__who">
          {post.mine ? <span className="student-dpost__you">You</span> : null}
          <span className="student-dpost__name">{post.authorName}</span>
        </span>
        <time className="student-dpost__when" dateTime={post.at}>
          {whenLabel(post.at)}
        </time>
      </header>
      <p className="student-dpost__body">{post.body}</p>
      <div className="student-dpost__tools">
        {allowReply && onReply && !open ? (
          <button type="button" className="student-text-link" onClick={() => setOpen(true)}>
            Reply
          </button>
        ) : null}
        {replyCount > 0 ? (
          <button
            type="button"
            className={`student-text-link${showReplies ? " is-on" : ""}`}
            aria-expanded={showReplies}
            onClick={() => setShowReplies((on) => !on)}
          >
            {replyCount === 1 ? "1 reply" : `${replyCount} replies`}
          </button>
        ) : null}
      </div>
      {allowReply && onReply && open ? (
        <div className="student-dform student-dform--reply">
          <textarea
            className="student-dform__input"
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Reply to this post"
            disabled={busy}
          />
          <div className="student-dform__actions">
            <button type="button" className="student-text-link" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="student-btn"
              disabled={busy || !reply.trim()}
              onClick={() => void send()}
            >
              {busy ? "Posting…" : "Reply"}
            </button>
          </div>
        </div>
      ) : null}
      {showReplies && replyCount > 0 ? (
        <div
          className="student-dpost__replies"
          role="group"
          aria-label={replyCount === 1 ? "1 reply" : `${replyCount} replies`}
        >
          {replies.map((child) => (
            <PostCard key={child.id} post={child} allowReply={allowReply} onReply={onReply} nested />
          ))}
        </div>
      ) : null}
    </article>
  );
}

export default function StudentDiscussion({
  activityId,
  discussion,
  onChange,
  disabled,
}: {
  activityId: string;
  discussion: DiscussionState;
  onChange: (next: DiscussionState) => void;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const board = discussion.board;

  const publish = async (body: string, parentId?: string) => {
    setBusy(true);
    setError("");
    try {
      const env = await post<{ success: boolean; data: DiscussionState }>(
        `/api/student/activities/${encodeURIComponent(activityId)}/discussion`,
        { body, parentId: parentId || null },
      );
      onChange(env.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not post");
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const postPrompt = async () => {
    if (!draft.trim() || busy || disabled) return;
    try {
      await publish(draft);
      setDraft("");
    } catch {
      /* error already set */
    }
  };

  return (
    <div className="student-discussion">
      <div className="student-section__head">
        <h2>Discussion</h2>
      </div>

      {!discussion.postedPrompt && board.requirePromptBeforeViewing ? (
        <p className="student-notice" style={{ marginTop: 8 }}>
          Post your response to see what classmates wrote.
        </p>
      ) : null}

      {discussion.postedPrompt || !board.allowPromptResponse ? null : (
        <div className="student-dform" style={{ marginTop: 12 }}>
          <label className="student-vh" htmlFor="discussion-prompt">
            Your response
          </label>
          <textarea
            id="discussion-prompt"
            className="student-dform__input"
            rows={5}
            value={draft}
            disabled={busy || disabled}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Write your response"
          />
          <div className="student-dform__actions">
            <button
              type="button"
              className="student-btn student-btn--primary"
              disabled={busy || disabled || !draft.trim()}
              onClick={() => void postPrompt()}
            >
              {busy ? "Posting…" : "Post response"}
            </button>
          </div>
        </div>
      )}

      {error ? (
        <div className="student-inline-error" role="alert" style={{ marginTop: 10 }}>
          {error}
        </div>
      ) : null}

      {discussion.posts.length > 0 ? (
        <div className="student-dthread">
          {discussion.posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              allowReply={Boolean(board.allowReplyToOthers && discussion.postedPrompt && !disabled)}
              onReply={async (parentId, body) => {
                await publish(body, parentId);
              }}
            />
          ))}
        </div>
      ) : discussion.postedPrompt ? (
        <p className="student-muted" style={{ marginTop: 12 }}>
          You posted. Classmates’ replies will show here.
        </p>
      ) : null}
    </div>
  );
}
